import type { ExportFormat } from "@/modules/export";

/** Opção de um select de filtro: o rótulo na tela e o valor enviado na consulta. */
export interface AuditFilterOption {
  value: string;
  label: string;
}

/**
 * Alterações auditadas hoje pelo Hub. O valor é o código combinado com o
 * serviço e é o que segue na consulta; o rótulo é o texto da tela.
 */
export const AUDIT_ACTIONS = [
  "USUARIO_CRIADO",
  "USUARIO_ATUALIZADO",
  "USUARIO_PERFIL_ATUALIZADO",
  "USUARIO_STATUS_ATUALIZADO",
  "PERFIL_CRIADO",
  "PERFIL_ATUALIZADO",
  "PERFIL_EXCLUIDO",
  "PERMISSAO_ATUALIZADA",
  "EXPORTACAO",
  "IMPRESSAO",
  "FICHA_FINANCEIRA_ANEXO_INCLUIDO",
  "FICHA_FINANCEIRA_GERADA",
] as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[number];

/** Módulos do sistema cobertos pela Auditoria, no mesmo contrato de código/rótulo. */
export const AUDIT_MODULES = [
  "INICIO",
  "OBRAS",
  "RECURSOS",
  "EMBARCACOES",
  "FINANCEIRO",
  "PAINEL_CONTROLE",
] as const;

export type AuditModule = (typeof AUDIT_MODULES)[number];

export const AUDIT_MODULE_LABELS: Record<AuditModule, string> = {
  INICIO: "Início",
  OBRAS: "Obras",
  RECURSOS: "Recursos",
  EMBARCACOES: "Embarcações",
  FINANCEIRO: "Financeiro",
  PAINEL_CONTROLE: "Painel de Controle",
};

export const AUDIT_MODULE_OPTIONS: AuditFilterOption[] = AUDIT_MODULES.map((value) => ({
  value,
  label: AUDIT_MODULE_LABELS[value],
}));

/** Código bruto do módulo (`modulo`) para o módulo do sistema, quando o serviço não envia `moduloSistema`. */
const RAW_MODULE_TO_SYSTEM_MODULE: Record<string, AuditModule> = {
  USUARIOS: "PAINEL_CONTROLE",
  PERFIS: "PAINEL_CONTROLE",
  PERMISSOES: "PAINEL_CONTROLE",
  AUDITORIA: "PAINEL_CONTROLE",
  AUTENTICACAO: "INICIO",
  OBRAS: "OBRAS",
  RECURSOS: "RECURSOS",
};

/** Rótulo de um código de módulo (do sistema ou bruto); texto desconhecido segue como veio. */
export function formatAuditModule(modulo: string): string {
  const key = modulo.trim().toUpperCase();
  const labels = AUDIT_MODULE_LABELS as Record<string, string>;
  const system = labels[key] ? key : RAW_MODULE_TO_SYSTEM_MODULE[key];

  return (system && labels[system]) || modulo;
}

/** Módulo exibido do registro: `moduloSistema`, com fallback no `modulo` bruto. */
export function getAuditModuleLabel(record: Pick<AuditRecord, "modulo" | "moduloSistema">): string {
  if (record.moduloSistema.trim()) return formatAuditModule(record.moduloSistema);

  return record.modulo ? formatAuditModule(record.modulo) : "";
}

const JUSTIFICATIVA_RECURSO_LABELS: Record<string, string> = {
  usuarios: "Usuários",
  perfis: "Perfis",
  permissoes: "Permissões",
  auditoria: "Auditoria",
  obras: "Obras",
  profissionais: "Recursos — Profissionais",
  "carga-profissionais": "Carga de trabalho — Profissionais",
  "alocacoes/profissionais": "Carga de trabalho — Profissionais",
  "carga-obras-profissionais": "Carga de trabalho — Obras (profissionais)",
  "alocacoes/obras/profissionais": "Carga de trabalho — Obras (profissionais)",
  "carga-locais": "Carga de trabalho — Locais",
  "alocacoes/locais": "Carga de trabalho — Locais",
  "carga-obras-locais": "Carga de trabalho — Obras (locais)",
  "alocacoes/obras/locais": "Carga de trabalho — Obras (locais)",
  "carga-fornecedores": "Carga de trabalho — Fornecedores",
  "alocacoes/fornecedores": "Carga de trabalho — Fornecedores",
  "carga-obras-fornecedores": "Carga de trabalho — Obras (fornecedores)",
  "alocacoes/obras/fornecedores": "Carga de trabalho — Obras (fornecedores)",
  embarcacoes: "Embarcações",
  "embarcacoes/detalhamento": "Embarcações – Detalhamento",
  "financeiro-obras": "Financeiro",
  "financeiro-fichas": "Ficha Financeira da Obra",
};

/** Valor preenchido de um filtro legado (objeto): listas unidas por ", "; vazio fica de fora. */
function valorDoFiltro(valor: unknown): string {
  if (Array.isArray(valor)) return valor.map(String).join(", ");
  if (valor === undefined || valor === null) return "";

  return String(valor);
}

/** Parte "Filtros: …" (lista de textos ou objeto legado), ou "Sem filtros"; null sem a chave (registro antigo). */
function formatAuditFiltros(filtros: unknown): string | null {
  if (filtros === undefined || filtros === null) return null;

  const linhas = Array.isArray(filtros)
    ? filtros.filter((linha): linha is string => typeof linha === "string" && linha.trim() !== "")
    : typeof filtros === "object"
      ? Object.entries(filtros)
          .filter(([campo]) => campo !== "colunas")
          .map(([campo, valor]) => [campo, valorDoFiltro(valor)])
          .filter(([, valor]) => valor !== "")
          .map(([campo, valor]) => `${campo}: ${valor}`)
      : [];

  return linhas.length ? `Filtros: ${linhas.join("; ")}` : "Sem filtros";
}

/**
 * Fallback de dado antigo, sem `justificativaLegivel`. Em EXPORTACAO/IMPRESSAO a justificativa chega como JSON
 * ({"recurso","formato","quantidade","filtros"}) e vira "Recurso: X | Formato: Y | Quantidade: N | Filtros: Campo: valor; …"
 * (ou "Sem filtros"), o mesmo formato do core; qualquer outro caso mantém o texto original.
 */
export function formatAuditJustificativa(
  record: Pick<AuditRecord, "acao" | "justificativa">,
): string {
  const { justificativa } = record;
  if (record.acao !== "EXPORTACAO" && record.acao !== "IMPRESSAO") return justificativa;

  try {
    const parsed: unknown = JSON.parse(justificativa);
    if (typeof parsed !== "object" || parsed === null) return justificativa;

    const { recurso, formato, quantidade, filtros } = parsed as Record<string, unknown>;
    if (
      typeof recurso !== "string" ||
      typeof formato !== "string" ||
      (typeof quantidade !== "number" && typeof quantidade !== "string")
    ) {
      return justificativa;
    }

    return [
      `Recurso: ${JUSTIFICATIVA_RECURSO_LABELS[recurso.trim().toLowerCase()] ?? recurso}`,
      `Formato: ${formato.toUpperCase()}`,
      `Quantidade: ${quantidade}`,
      formatAuditFiltros(filtros),
    ]
      .filter(Boolean)
      .join(" | ");
  } catch {
    return justificativa;
  }
}

/** Justificativa da tela: a do serviço (`justificativaLegivel`) e, em dado antigo, o texto formatado aqui. */
export function resolveAuditJustificativa(
  record: Pick<AuditRecord, "acao" | "justificativa" | "justificativaLegivel">,
): string {
  return record.justificativaLegivel || formatAuditJustificativa(record);
}

export interface AuditRecord {
  id: string;
  /** Data/hora do evento em ISO-8601; texto original quando o serviço não devolve ISO. */
  dataHora: string;
  acao: string;
  /** Texto da Ação para a tela, montado pelo serviço; vazio em dado antigo (a tela cai no código). */
  acaoDescricao: string;
  /** Valor antes da alteração; vazio nas ações que não trocam um valor (ex.: login). */
  valorAnterior: string;
  /** Valor depois da alteração; acompanha `valorAnterior` na coluna Ação. */
  valorNovo: string;
  justificativa: string;
  /** Texto pronto para a tela, montado pelo serviço (ex.: o JSON da exportação por extenso); vazio em dado antigo. */
  justificativaLegivel: string;
  /** Código bruto do módulo devolvido pelo serviço. */
  modulo: string;
  /** Módulo do sistema (INICIO, OBRAS, ...); vazio quando o serviço não devolve. */
  moduloSistema: string;
  responsavel: string;
  /** Cargo do responsável; vazio quando não há. */
  responsavelCargo: string;
  /** Perfil do responsável; vazio quando não há. */
  responsavelPerfil: string;
  /**
   * Cargo, perfil ou área do responsável: aparece abaixo do nome na listagem e
   * como o cargo do usuário no detalhamento. Vazio quando o serviço não devolve.
   */
  responsavelDetalhe: string;
  /** E-mail do responsável, exibido no detalhamento; vazio quando não há. */
  responsavelEmail: string;
  /** Telefone do responsável, exibido no detalhamento; vazio quando não há. */
  responsavelTelefone: string;
  /**
   * Registro afetado pela alteração (ex.: o usuário ou o perfil alterado),
   * distinto do responsável, que é quem executou a ação. Vazio quando o
   * serviço não devolve `registroAlterado` (ex.: login).
   */
  registroAlteradoTipo: string;
  registroAlteradoNome: string;
  registroAlteradoEmail: string;
  registroAlteradoCodigo: string;
  /** Obra em que o registro foi alterado ("código - nome"); "" quando não se aplica. */
  registroAlteradoObra: string;
}

/** Rótulo amigável do tipo do registro afetado; mantém o valor original quando não mapeado. */
const REGISTRO_ALTERADO_TIPO_LABELS: Record<string, string> = {
  usuario: "Usuário",
  perfil: "Perfil",
  /** Profissional da alocação na equipe de obra (HU 2051). */
  profissional: "Profissional",
};

export function formatRegistroAlteradoTipo(tipo: string): string {
  const key = tipo.trim().toLowerCase();
  return REGISTRO_ALTERADO_TIPO_LABELS[key] ?? tipo;
}

/** Critérios do painel de filtros; combinados entre si (RN003). */
export interface AuditFilters {
  /** Início do período, `AAAA-MM-DD`; "" sem período. O período só vale completo (início e fim). */
  dataInicio: string;
  /** Fim do período, `AAAA-MM-DD`; "" sem período. */
  dataFim: string;
  /** Códigos das alterações auditadas; vazio quando o critério não está aplicado. */
  alteracao: AuditAction[];
  /** Código do módulo auditado; "" quando o critério não está aplicado. */
  modulo: AuditModule | "";
  /** Nomes dos usuários escolhidos no filtro de Responsável; vazio quando o critério não está aplicado. */
  responsavelId: string[];
}

export const EMPTY_AUDIT_FILTERS: AuditFilters = {
  dataInicio: "",
  dataFim: "",
  alteracao: [],
  modulo: "",
  responsavelId: [],
};

/** Há critério além do padrão (sem padrão: além de nenhum), comparado campo a campo; listas na ordem dos valores. */
export function hasAuditFilters(
  filters: AuditFilters,
  padrao: AuditFilters = EMPTY_AUDIT_FILTERS,
): boolean {
  return (Object.keys(filters) as (keyof AuditFilters)[]).some((campo) => {
    const valor = filters[campo];
    const base = padrao[campo];
    return Array.isArray(valor) && Array.isArray(base)
      ? valor.length !== base.length || valor.some((item, indice) => item !== base[indice])
      : valor !== base;
  });
}

export type AuditSortOrder = "asc" | "desc";

/**
 * Colunas de dado ordenáveis pelo cabeçalho. Os Padrões Globais de Interface
 * preveem ordenação em todas as colunas do grid; a coluna de comandos da linha
 * (Detalhar) não carrega dado comparável e por isso fica de fora.
 */
export type AuditSortField = "dataHora" | "acao" | "justificativa" | "modulo" | "responsavel";

export const AUDIT_SORT_FIELDS: AuditSortField[] = [
  "dataHora",
  "acao",
  "justificativa",
  "modulo",
  "responsavel",
];

/** Critério de ordenação em vigor: a coluna e a direção aplicadas (RN01/RN02). */
export interface AuditSort {
  field: AuditSortField;
  direction: AuditSortOrder;
}

/**
 * Ordem padrão da tela, aplicada quando nenhuma coluna está ordenada: Data/Hora
 * do mais recente para o mais antigo.
 */
export const DEFAULT_AUDIT_SORT: AuditSort = { field: "dataHora", direction: "desc" };

/** Leitura tolerante do campo devolvido pelo cabeçalho da tabela. */
export function isAuditSortField(value: unknown): value is AuditSortField {
  return typeof value === "string" && AUDIT_SORT_FIELDS.includes(value as AuditSortField);
}

export interface ListAuditRecordsParams extends Partial<AuditFilters> {
  page?: number;
  pageSize?: number;
  /** Coluna e direção que ordenam o conjunto filtrado; o padrão é Data/Hora, `desc` (mais recente primeiro). */
  sort?: AuditSort;
  signal?: AbortSignal;
}

/** Critérios da visualização atual, sem paginação (RN003 da 1442), e o formato de saída. */
export interface ExportAuditRecordsParams extends Partial<AuditFilters> {
  sort?: AuditSort;
  /** Linhas do resumo no arquivo (filtros em vigor). */
  filtros?: string[];
  /** Colunas visíveis da tela, na ordem; o gateway as traduz para as chaves do core. */
  colunas?: readonly AuditColumnKey[];
  format: ExportFormat;
  signal?: AbortSignal;
}

export interface AuditRecordsPage {
  records: AuditRecord[];
  total: number;
}

/** Consulta somente leitura do registro acionado em Detalhar (RN001). */
export interface GetAuditRecordParams {
  id: string;
  signal?: AbortSignal;
}

/**
 * Estados do detalhamento. Cada cenário de falha tem tratamento próprio:
 * `sessionExpired` (401), `forbidden` (403), `notFound` (404) e `error` para a
 * falha de comunicação.
 */
export type AuditDetailStatus =
  "loading" | "success" | "sessionExpired" | "forbidden" | "notFound" | "error";

export interface AuditDetailState {
  status: AuditDetailStatus;
  /** Preenchido apenas em `success`: nenhuma falha exibe dados do registro. */
  record: AuditRecord | null;
  errorMessage: string | null;
}

/** Colunas da tela de Auditoria, na ordem da tabela (a exportação traduz cada uma para a chave do core). */
export type AuditColumnKey = AuditSortField | "acoes";
